import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import LaTeXRenderer from '../LaTeXRenderer';
import API_BASE_URL from '../../config/api';
import SubmissionDetail from './SubmissionDetail';

const SubmissionGrader = ({ 
  submission, 
  maxScore = 7, 
  onFeedbackUpdated, 
  onViewFile,
  showFiles = true,
  compact = false 
}) => {
  const { user: currentUser } = useAuth();
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackScore, setFeedbackScore] = useState('');
  const [score, setScore] = useState(submission?.score || '');
  const [showGrading, setShowGrading] = useState(false);
  const [editingFeedbackId, setEditingFeedbackId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Find current user's feedback
  const userFeedback = submission?.feedback?.find(
    f => f.gradedBy?._id === currentUser?._id
  );
  
  const hasUserFeedback = !!userFeedback;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleUpdateFeedback = async (feedbackId) => {
    if (!feedbackText.trim()) {
      alert('Feedback text cannot be empty');
      return;
    }

    if (feedbackScore !== '' && (isNaN(feedbackScore) || parseFloat(feedbackScore) < 0 || parseFloat(feedbackScore) > maxScore)) {
      alert(`Score must be between 0 and ${maxScore}`);
      return;
    }

    console.log('Updating feedback:', { feedbackId, submissionId: submission._id }); // Debug log

    setIsSubmitting(true);
    try {
      const payload = {
        text: feedbackText.trim()
      };
      
      // Include score if provided
      if (feedbackScore !== '') {
        payload.score = parseFloat(feedbackScore);
      }

      console.log('API call payload:', payload); // Debug log
      const response = await axios.put(`${API_BASE_URL}/api/submissions/${submission._id}/feedback/${feedbackId}`, payload);

      // Reset editing state
      setEditingFeedbackId(null);
      setFeedbackText('');
      setFeedbackScore('');
      
      // Notify parent component - use the submission from response
      if (onFeedbackUpdated) {
        onFeedbackUpdated(response.data.submission || response.data);
      }
      
      alert('Feedback updated successfully!');
    } catch (error) {
      console.error('Update feedback error:', error.response?.data || error.message);
      alert(error.response?.data?.message || 'Failed to update feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteFeedback = async (feedbackId) => {
    if (!window.confirm('Are you sure you want to delete this feedback? This action cannot be undone.')) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.delete(`${API_BASE_URL}/api/submissions/${submission._id}/feedback/${feedbackId}`);
      
      // Notify parent component - use the submission from response
      if (onFeedbackUpdated) {
        onFeedbackUpdated(response.data.submission || response.data);
      }
      
      alert('Feedback deleted successfully!');
    } catch (error) {
      console.error('Delete feedback error:', error.response?.data || error.message);
      alert(error.response?.data?.message || 'Failed to delete feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitGrade = async (e) => {
    e.preventDefault();
    
    if (!hasUserFeedback && !feedbackText.trim()) {
      alert('Please provide feedback text');
      return;
    }

    if (!hasUserFeedback && (feedbackScore === '' || isNaN(feedbackScore))) {
      alert('Please enter a valid score for your feedback');
      return;
    }

    if (!hasUserFeedback && (parseFloat(feedbackScore) < 0 || parseFloat(feedbackScore) > maxScore)) {
      alert(`Score must be between 0 and ${maxScore}`);
      return;
    }

    setIsSubmitting(true);
    try {
      let response;
      
      if (hasUserFeedback) {
        // Admin updating overall score only
        if (score === '' || isNaN(score)) {
          alert('Please enter a valid score');
          return;
        }
        if (parseFloat(score) < 0 || parseFloat(score) > maxScore) {
          alert(`Score must be between 0 and ${maxScore}`);
          return;
        }
        
        response = await axios.put(`${API_BASE_URL}/api/admin/submissions/${submission._id}/grade`, {
          score: parseFloat(score),
          feedbackText: userFeedback.text
        });
      } else {
        // Adding new feedback with per-feedback score
        response = await axios.post(`${API_BASE_URL}/api/submissions/${submission._id}/feedback`, {
          text: feedbackText.trim(),
          score: parseFloat(feedbackScore)
        });
      }

      // Notify parent component
      if (onFeedbackUpdated) {
        onFeedbackUpdated(response.data);
      }

      setShowGrading(false);
      setFeedbackText('');
      setFeedbackScore('');
      alert('Grade saved successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to save grade');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!submission) {
    return (
      <div className="card">
        <h4 style={{ margin: '0 0 12px 0', color: '#666' }}>No Submission</h4>
        <p style={{ margin: 0, color: '#666' }}>
          This student has not yet submitted a solution for this problem.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* ── Read-only content: files, text, OCR, AI, status badges ── */}
      {showFiles && (
        <div style={{ marginBottom: '16px' }}>
          <SubmissionDetail
            submission={submission}
            maxScore={maxScore}
            onViewFile={onViewFile}
            currentUser={currentUser}
            hideFeedback={true}
          />
        </div>
      )}

      {/* ── Grader-editable Feedback ── */}
      {/* Current Grade and Feedback */}
      {submission.feedback && submission.feedback.length > 0 && (
        <div className="card" style={{ backgroundColor: '#e7f3ff', marginBottom: '16px' }}>
          <h5 style={{ margin: '0 0 12px 0' }}>
            Feedback ({submission.feedback.length} comment{submission.feedback.length !== 1 ? 's' : ''})
          </h5>
          {submission.status === 'pending_decision' ? (
            <div style={{ marginBottom: '16px', padding: '8px', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107' }}>
              <strong style={{ color: '#856404' }}>⚠️ Pending Decision: Graders disagree on score - consensus required</strong>
            </div>
          ) : submission.score !== null ? (
            <div style={{ marginBottom: '16px', padding: '8px', backgroundColor: '#d4edda', borderRadius: '4px' }}>
              <strong>🎯 Final Consensus Score: {submission.score}/{maxScore}</strong>
              {submission.feedback && submission.feedback.length > 1 && (
                <div style={{ fontSize: '12px', color: '#155724', marginTop: '4px' }}>
                  ✓ All {submission.feedback.length} graders agreed on this score
                </div>
              )}
            </div>
          ) : null}
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {submission.feedback.map((feedback) => {
              const isMyFeedback = feedback.gradedBy?._id === currentUser?._id;
              const isCurrentlyEditing = editingFeedbackId === feedback._id;
              
              return (
                <div key={feedback._id} style={{ 
                  border: isMyFeedback ? '2px solid #28a745' : '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '12px',
                  backgroundColor: 'white'
                }}>
                  {isCurrentlyEditing ? (
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      handleUpdateFeedback(feedback._id);
                    }}>
                      <div className="form-group">
                        <label className="form-label">Edit Your Feedback</label>
                        <textarea
                          value={feedbackText}
                          onChange={(e) => setFeedbackText(e.target.value)}
                          className="form-control"
                          rows="3"
                          required
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Your Score (0-{maxScore})</label>
                        <input
                          type="number"
                          value={feedbackScore}
                          onChange={(e) => setFeedbackScore(e.target.value)}
                          className="form-control"
                          min="0"
                          step="1"
                          max={maxScore}
                          placeholder={`Enter your score (0-${maxScore})`}
                          disabled={isSubmitting}
                          style={{ width: '120px' }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                        <button 
                          type="submit" 
                          className="btn btn-success" 
                          style={{ fontSize: '12px' }}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button 
                          type="button" 
                          onClick={() => {
                            setEditingFeedbackId(null);
                            setFeedbackText('');
                            setFeedbackScore('');
                          }}
                          className="btn btn-secondary" 
                          style={{ fontSize: '12px' }}
                          disabled={isSubmitting}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <div style={{ marginBottom: '8px' }}>
                        <LaTeXRenderer text={feedback.text} />
                      </div>
                      <div style={{ 
                        marginBottom: '8px', 
                        padding: '4px 8px', 
                        backgroundColor: feedback.score !== null && feedback.score !== undefined ? '#e8f5e8' : '#f8f9fa', 
                        borderRadius: '4px', 
                        fontSize: '14px', 
                        fontWeight: 'bold',
                        color: feedback.score !== null && feedback.score !== undefined ? '#155724' : '#666',
                        display: 'inline-block'
                      }}>
                        Score: {feedback.score !== null && feedback.score !== undefined ? `${feedback.score}/${maxScore}` : 'Not scored'}
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        fontSize: '12px', 
                        color: '#666' 
                      }}>
                        <div>
                          <strong>{feedback.gradedBy?.name}</strong>
                          {feedback.gradedBy?.role && (
                            <span> ({feedback.gradedBy.role})</span>
                          )}
                          <span style={{ marginLeft: '8px' }}>
                            {formatDate(feedback.gradedAt)}
                          </span>
                          {isMyFeedback && (
                            <span style={{ 
                              marginLeft: '8px', 
                              color: '#28a745', 
                              fontWeight: 'bold' 
                            }}>
                              (Your feedback)
                            </span>
                          )}
                        </div>
                        {(isMyFeedback || currentUser?.role === 'admin') && (
                          <div style={{ display: 'flex', gap: '4px' }}>
                            {(isMyFeedback || currentUser?.role === 'admin') && (
                              <button
                                onClick={() => {
                                  setEditingFeedbackId(feedback._id);
                                  setFeedbackText(feedback.text);
                                  setFeedbackScore(feedback.score !== null && feedback.score !== undefined ? feedback.score.toString() : '');
                                }}
                                className="btn btn-outline"
                                style={{ fontSize: '11px', padding: '2px 6px' }}
                                disabled={isSubmitting}
                                title={isMyFeedback ? "Edit your feedback" : "Edit this feedback (Admin)"}
                              >
                                Edit
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteFeedback(feedback._id)}
                              className="btn"
                              style={{ 
                                fontSize: '11px', 
                                padding: '2px 6px',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none'
                              }}
                              disabled={isSubmitting}
                              title={isMyFeedback ? "Delete your feedback" : "Delete this feedback (Admin)"}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Add New Feedback / Grade Interface */}
      <div>
        {hasUserFeedback ? (
          // User already has feedback, show "Update Score" if they're admin
          currentUser?.role === 'admin' && !showGrading && (
            <button
              onClick={() => {
                setShowGrading(true);
                setFeedbackText('');
                setScore(submission.score || '');
              }}
              className="btn btn-warning"
              style={{ fontSize: compact ? '12px' : '14px' }}
              disabled={isSubmitting}
            >
              Update Score Only
            </button>
          )
        ) : (
          // User hasn't provided feedback yet
          <button
            onClick={() => {
              setShowGrading(true);
              setFeedbackText('');
              setScore(submission.score || '');
            }}
            className="btn btn-primary"
            style={{ fontSize: compact ? '12px' : '14px' }}
            disabled={isSubmitting}
          >
            {submission.feedback?.length > 0 ? 'Add Your Feedback' : 'Grade Submission'}
          </button>
        )}

        {showGrading && (
          <form onSubmit={handleSubmitGrade} className="card" style={{ backgroundColor: '#f8f9fa', marginTop: '12px' }}>
            <h5 style={{ margin: '0 0 16px 0', fontSize: compact ? '14px' : '16px' }}>
              {hasUserFeedback ? 'Update Overall Score' : 'Add Your Grade & Feedback'}
            </h5>
            
            {hasUserFeedback ? (
              // Admin can update overall score only
              <div className="form-group">
                <label className="form-label">Overall Submission Score</label>
                <input
                  type="number"
                  value={score}
                  onChange={(e) => setScore(e.target.value)}
                  className="form-control"
                  min="0"
                  step="1"
                  max={maxScore}
                  placeholder={`Enter score (0-${maxScore})`}
                  required
                  disabled={isSubmitting}
                  style={{ width: '120px' }}
                />
              </div>
            ) : (
              // New feedback with individual score
              <>
                <div className="form-group">
                  <label className="form-label">Your Score (0-{maxScore}) *</label>
                  <input
                    type="number"
                    value={feedbackScore}
                    onChange={(e) => setFeedbackScore(e.target.value)}
                    className="form-control"
                    min="0"
                    step="1"
                    max={maxScore}
                    placeholder={`Enter your score (0-${maxScore})`}
                    required
                    disabled={isSubmitting}
                    style={{ width: '120px' }}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Your Feedback *</label>
                  <textarea
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    className="form-control"
                    rows="3"
                    placeholder="Provide your feedback to the student..."
                    required
                    disabled={isSubmitting}
                  />
                </div>
              </>
            )}

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="submit"
                className="btn btn-success"
                disabled={isSubmitting}
                style={{ fontSize: compact ? '12px' : '14px' }}
              >
                {isSubmitting ? (
                  <span>
                    <div className="spinner" style={{ 
                      width: '12px', 
                      height: '12px', 
                      display: 'inline-block', 
                      marginRight: '6px' 
                    }}></div>
                    Saving...
                  </span>
                ) : (
                  hasUserFeedback ? 'Update Score' : 'Save Grade & Feedback'
                )}
              </button>
              
              <button
                type="button"
                onClick={() => {
                  setShowGrading(false);
                  setFeedbackText('');
                }}
                className="btn btn-secondary"
                style={{ fontSize: compact ? '12px' : '14px' }}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SubmissionGrader;
